import React from 'react'
import styled, { css } from 'styled-components'
import { mapLanguageToComponentProps } from 'admin/i18n'
import { connect } from 'react-redux'
import {
  Subtitle,
  Label,
  Body,
  Ellipsis,
  LineClamp,
} from 'components/styled/typography'
import { State, States } from 'components/styled/state'
import { PositionType } from 'types'
import { imagePath } from 'utils/image'
import moment from 'moment-timezone'
import { Icon } from 'components/styled/icon'
import { ThemeColor } from 'theme/colors'
import { I18nComponentType } from 'admin/i18n-types'
import defaultHero from '../../../assets/default-hero-image.jpg'
import { Box } from '../containers/box'
import { Flex } from '../containers/flex'

export interface PositionCardProps {
  onClick: () => void
  addNewText?: string
  positionData?: PositionType
  dateLabel?: string
  leftCountColumn?: {
    label: string
    count: number
  }
  rightCountColumn?: {
    label: string
    count: number
  }
  overlayColor?: ThemeColor
  overlayOpacity?: number
  isMultiCompaniesBelonging?: boolean
  hasUnseenVideos?: boolean
  i18n: I18nComponentType
}

const AddNewStyle = css`
  justify-content: center;
  align-items: center;
  padding: ${({ theme }) => theme.space.s96} 0;
`

const StyledPositionCard = styled.button<{
  $addNew: boolean
  $hasUnseenVideos?: boolean
}>`
  all: unset;
  display: flex;
  cursor: pointer;
  width: 100%;
  border: ${({ theme, $hasUnseenVideos }) =>
    $hasUnseenVideos
      ? `${theme.space.s2} solid ${theme.color.primary110}`
      : 'none'};
  background-color: ${({ theme, $addNew }) =>
    $addNew ? theme.color.grey05 : theme.color.grey07};
  border-radius: ${({ theme }) => theme.space.s8};
  flex-direction: column;
  user-select: none;

  &:hover {
    background-color: ${({ theme }) => theme.color.grey10};
  }

  &:active {
    background-color: ${({ theme }) => theme.color.grey20};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.color.grey05};
    color: ${({ theme }) => theme.color.grey30};
  }

  &.focus-visible {
    box-shadow: 0 0 0 1px ${({ theme }) => theme.color.white},
      0 0 0 3px ${({ theme }) => theme.color.grey70};
  }

  ${({ $addNew }) => $addNew && AddNewStyle};
`

const PositionCardContainer = styled.div`
  position: relative;
`
const NewVideosInfo = styled.div`
  position: absolute;
  top: -${({ theme }) => theme.space.s8};
  right: ${({ theme }) => theme.space.s4};
  z-index: 1;
  width: fit-content;
  height: auto;
  padding: 0 ${({ theme }) => theme.space.s4};
  border-radius: ${({ theme }) => theme.space.s4};
  background-color: ${({ theme }) => theme.color.primary110};
`

export const PhotoContainer = styled.div<{ $image: string }>`
  display: flex;
  align-items: flex-end;
  background-image: url(${({ $image }) => $image});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  height: ${({ theme }) => theme.space.s160};
  border-radius: ${({ theme }) => theme.space.s8}
    ${({ theme }) => theme.space.s8} 0 0;
  position: relative;
`

const StatusContainer = styled.div`
  display: flex;
  align-items: center;
  height: fit-content;
  flex-direction: column;
  align-items: flex-start;
  background: ${({ theme }) => theme.color.blackO70};
  width: 100%;
  color: ${({ theme }) => theme.color.white};
  padding: ${({ theme }) => theme.space.s4} ${({ theme }) => theme.space.s12};
  z-index: 2;

  ${({ theme }) => theme.mediaQuery.tablet} {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`
const StyledLabel = styled(Label)`
  color: ${({ theme }) => theme.color.white};
  margin: 0;
`

const StyledDateTime = styled(Body)`
  text-align: right;
`

export const PositionDetails = styled(Box)`
  height: ${({ theme }) => theme.space.s96};
`

export const StyledInfoContainer = styled.div`
  padding: ${({ theme }) => theme.space.s12};
`

export const PositionTitle = styled(Subtitle)`
  ${Ellipsis};
  ${({ numberOfLines }) => numberOfLines && LineClamp};
  margin-bottom: ${({ theme }) => theme.space.s8};
`

export const PositionLocation = styled(Body)`
  ${Ellipsis};
  ${({ numberOfLines }) => numberOfLines && LineClamp};
  margin-bottom: ${({ theme }) => theme.space.s16};
`

export const PositionCompany = styled(Body)`
  ${Ellipsis};
  ${({ numberOfLines }) => numberOfLines && LineClamp};
  padding-top: ${({ theme }) => theme.space.s16};
  border-top: 1px solid ${({ theme }) => theme.color.grey30};
`

const ColumnsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: end;
  padding-top: ${({ theme }) => theme.space.s16};
`

const StyledColumn = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 1 50%;
`

const AddNewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: ${({ theme }) => theme.space.s16};
`

const Overlay = styled.div<{ $color: ThemeColor; $opacity: number }>`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  border-radius: ${({ theme }) => theme.space.s8}
    ${({ theme }) => theme.space.s8} 0 0;
  background-color: ${({ theme, $color }) => theme.color[$color]};
  opacity: ${({ $opacity }) => $opacity};
`

const StatusAndIconContainer = styled(Flex)`
  align-items: center;
  justify-content: space-between;
  width: 100%;

  ${({ theme }) => theme.mediaQuery.tablet} {
    width: auto;
  }
`

export const PositionCardFC: React.FC<PositionCardProps> = ({
  positionData,
  leftCountColumn,
  rightCountColumn,
  addNewText,
  dateLabel,
  onClick,
  overlayColor = 'black',
  overlayOpacity = 0.1,
  isMultiCompaniesBelonging,
  hasUnseenVideos = false,
  i18n,
}) => {
  const AddNewContent = () => (
    <AddNewContainer>
      <Icon type="add" size="s24" />
      <Subtitle>{addNewText}</Subtitle>
    </AddNewContainer>
  )

  const NewResponsesInfo = ({ text }: { text: string }) => {
    return (
      <NewVideosInfo>
        <Body color="white">{text}</Body>
      </NewVideosInfo>
    )
  }

  const PositionContent = () => {
    if (positionData) {
      const {
        photo,
        title,
        endTime,
        startTime,
        location,
        status,
        type,
        company,
        isEmbedded,
        isPublic,
      } = positionData
      const cardPhoto = photo ? imagePath(photo) : defaultHero
      const formattedDateTime = `${moment(endTime || startTime).format(
        'L'
      )} ${moment(endTime || startTime).format('LT')}`

      const isInterview = typeof type !== 'undefined'
      const interviewEndTimeIsInThePast = !!(
        isInterview &&
        endTime &&
        moment().diff(moment(endTime), 'days') > 0
      )
      const companyName = company && company.name ? company.name : undefined

      return (
        <>
          <PhotoContainer $image={cardPhoto}>
            <StatusContainer>
              {(status || (type && type === 'sequenced')) && (
                <StatusAndIconContainer>
                  {status && (
                    <State
                      state={
                        interviewEndTimeIsInThePast ? States.Closed : status
                      }
                      textColor="white"
                    />
                  )}
                  <Flex gap="s8" alignItems="center">
                    {type && type === 'sequenced' && (
                      <Icon useOutlined type="timer" color="white" size="s20" />
                    )}
                    {isPublic && (
                      <Icon
                        useOutlined
                        type="public"
                        color="white"
                        size="s20"
                        title={i18n.modalInterviewPublish.publicTitle}
                      />
                    )}
                    {isEmbedded && (
                      <Icon
                        useOutlined
                        type="code_blocks"
                        useSymbols
                        color="white"
                        size="s20"
                        title={i18n.modalInterviewPublish.title}
                      />
                    )}
                  </Flex>
                </StatusAndIconContainer>
              )}
              {dateLabel && <StyledLabel>{dateLabel}</StyledLabel>}
              {(endTime || startTime) && (
                <StyledDateTime color="white">
                  {formattedDateTime}
                </StyledDateTime>
              )}
            </StatusContainer>
            <Overlay $color={overlayColor} $opacity={overlayOpacity} />
          </PhotoContainer>
          <StyledInfoContainer>
            <PositionDetails>
              {title && (
                <PositionTitle numberOfLines={2}>{title}</PositionTitle>
              )}
              {location && (
                <PositionLocation numberOfLines={1}>
                  {location}
                </PositionLocation>
              )}
            </PositionDetails>
            <ColumnsContainer>
              {leftCountColumn && (
                <StyledColumn>
                  <Label color="grey90">{leftCountColumn.label}</Label>
                  <Subtitle>{leftCountColumn.count}</Subtitle>
                </StyledColumn>
              )}
              {rightCountColumn && (
                <StyledColumn>
                  <Label color="grey90">{rightCountColumn.label}</Label>
                  <Subtitle>{rightCountColumn.count}</Subtitle>
                </StyledColumn>
              )}
            </ColumnsContainer>
            {companyName && isMultiCompaniesBelonging && (
              <PositionCompany numberOfLines={1}>{companyName}</PositionCompany>
            )}
          </StyledInfoContainer>
        </>
      )
    }
    return null
  }

  return (
    <PositionCardContainer>
      {hasUnseenVideos && (
        <NewResponsesInfo text={i18n.interviewRow.newVideos} />
      )}
      <StyledPositionCard
        $hasUnseenVideos={hasUnseenVideos}
        $addNew={!!addNewText}
        onClick={onClick}
      >
        {addNewText ? <AddNewContent /> : <PositionContent />}
      </StyledPositionCard>
    </PositionCardContainer>
  )
}

export const PositionCard = connect<{ i18n: I18nComponentType }>(
  mapLanguageToComponentProps()
)(PositionCardFC)
